import type { WorkflowDefinition, WorkflowToolCatalog } from './workflow.types';

type Definition = Pick<WorkflowDefinition, 'name' | 'description' | 'trigger' | 'nodes' | 'edges'>;
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const secretKey = /password|secret|api[_-]?key|access[_-]?token|refresh[_-]?token|authorization|credential|connectionstring/i;

// Credentials configured as named fields are removed. Free-form instructions are
// intentionally preserved; the preview lets the author review those before sharing.
function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact);
  if (!object(value)) return value;
  return Object.fromEntries(Object.entries(value).filter(([key]) => !['__proto__', 'constructor', 'prototype'].includes(key)).map(([key, entry]) => [key, secretKey.test(key) ? '[RECONNECT]' : redact(entry)]));
}

export function buildWorkflowPackage(workflow: Definition, catalog: WorkflowToolCatalog) {
  const used = new Set(workflow.nodes.map(node => node.subtype));
  used.add(workflow.trigger.type);
  return {
    format: 'prospo-workflow', version: 1, exportedAt: new Date().toISOString(),
    workflow: redact({ name: workflow.name, description: workflow.description || '', trigger: workflow.trigger, nodes: workflow.nodes, edges: workflow.edges }),
    tools: redact([...catalog.triggers, ...catalog.actions, ...catalog.logic].filter(def => used.has(def.subtype))),
    portability: {
      modelSettings: 'Managed by the destination server; not stored in workflow definitions.',
      skills: 'Skill IDs are included in step inputs. Skill contents are not bundled; reselect destination skills.',
      connections: 'Credentials are not bundled. Review client, meeting, template, folder and integration references before activation.',
    },
  };
}

export function parseWorkflowPackage(text: string, catalog: WorkflowToolCatalog): Definition {
  const data: unknown = JSON.parse(text);
  if (!object(data) || data.format !== 'prospo-workflow' || data.version !== 1 || !object(data.workflow)) throw new Error('Choose a Prospo workflow JSON package (version 1).');
  const w = data.workflow;
  if (typeof w.name !== 'string' || !w.name.trim() || (w.description !== undefined && typeof w.description !== 'string') || !object(w.trigger) || typeof w.trigger.type !== 'string' || !Array.isArray(w.nodes) || !Array.isArray(w.edges)) throw new Error('The workflow name, trigger, nodes or connections are invalid.');
  if (w.nodes.length > 1000 || w.edges.length > 5000) throw new Error('This workflow exceeds the import size limits.');
  const defs = [...catalog.triggers, ...catalog.actions, ...catalog.logic];
  const triggerType = w.trigger.type;
  if (!catalog.triggers.some(def => def.subtype === triggerType)) throw new Error(`Unavailable trigger: ${triggerType}`);
  const ids = new Set<string>();
  for (const node of w.nodes) {
    if (!object(node) || typeof node.id !== 'string' || !node.id || ids.has(node.id) || !['trigger', 'action', 'condition'].includes(String(node.type)) || typeof node.subtype !== 'string' || typeof node.x !== 'number' || !Number.isFinite(node.x) || typeof node.y !== 'number' || !Number.isFinite(node.y) || !object(node.config)) throw new Error('A workflow step is invalid or has a duplicate ID.');
    const category = node.type === 'condition' ? 'logic' : node.type;
    if (!defs.some(def => def.subtype === node.subtype && def.category === category)) throw new Error(`Unavailable tool or step: ${node.subtype}`);
    for (const [key, value] of Object.entries(node.config)) {
      if (key === 'args') {
        if (!object(value) || Object.values(value).some(v => typeof v !== 'string')) throw new Error('Step inputs must contain text values.');
      } else if (key === 'options') {
        if (!Array.isArray(value) || value.some(v => !object(v) || typeof v.id !== 'string' || typeof v.label !== 'string')) throw new Error('Condition options are invalid.');
      } else if (typeof value !== 'string') throw new Error(`Invalid step setting: ${key}`);
    }
    ids.add(node.id);
  }
  const edgeIds = new Set<string>();
  for (const edge of w.edges) {
    if (!object(edge) || typeof edge.id !== 'string' || !edge.id || edgeIds.has(edge.id) || typeof edge.source !== 'string' || !ids.has(edge.source) || typeof edge.target !== 'string' || !ids.has(edge.target) || typeof edge.sourcePort !== 'string' || !edge.sourcePort || edge.targetPort !== 'in') throw new Error('A workflow connection is invalid or references a missing step.');
    edgeIds.add(edge.id);
  }
  // Only definition fields are imported; identity, activation, and tool metadata
  // from the file cannot override the destination's identity or tool catalog.
  return { name: w.name.trim(), description: w.description as string | undefined, trigger: w.trigger, nodes: w.nodes, edges: w.edges } as Definition;
}
